import * as endpoints from "../../lib/api/endpoints";
import * as error from "../../lib/api/error";
import * as leadEvents from "../../constants/leadEvents";
import * as models from "../../lib/api/models";
import * as queryStringHelpers from "../../lib/queryStringHelpers";
import * as routes from "../../constants/routes";
import * as validationLib from "../../lib/validation";
import ApplicationForm from "../ApplicationForm";
import applicationToApplicationValidate, {
    DEFAULT_URL_CODE,
} from "../../lib/applicationToApplicationValidate";
import callApplicationLead from "../../lib/callApplicationLead";
import cn from "../../lib/classnames";
import createDebouncedAppLead from "../../lib/api/createDebouncedAppLead";
import DisclosureInfo from "../DisclosureInfo";
import ErrorBoundary from "../ErrorBoundary";
import Footer from "../Footer";
import getFirstInvalidKey, {ValidationType} from "../../lib/getFirstInvalidKey";
import getFullName from "../../lib/getFullName";
import getUserIds from "../../lib/getUserIds";
import getValidationKey from "../../lib/getValidationKey";
import hasFailingValidation from "../../lib/hasFailingValidation";
import Header from "../Header";
import IdleTimeoutWrapper from "../IdleTimeoutWrapper";
import isAddressKey from "../../lib/isAddressKey";
import isApplicationKey from "../../lib/isApplicationKey";
import LegalInfo from "../LegalInfo";
import React, {useState, useEffect, useRef} from "react";
import styles from "./OrganicApplicationPage.module.css";
import usePageView from "../../lib/usePageView";
import validateApplicationField from "../../lib/validateApplicationField";
import {AppKeys, AddressKeys, AddressValidationKeys, Application} from "../../lib/api/models";
import {getCards} from "../../lib/getCards";
import {RouteComponentProps} from "react-router-dom";
import {StaticContext} from "react-router";
import CardPicker from "../CardPicker";
import Spinner from "../Spinner";
import GalleryView from "../GalleryView";

const strings = {
    continue: "Continue",
    title: "1st Financial Bank USA Card Application",
};

const IS_DEV = process.env.NODE_ENV === "development";
const COMPONENT_NAME = "OrganicApplicationPage";

const DEFUNCT_OFFER_CODES = ["rhhA5s", "q4bB85", "qkeBMe"];

const EMPTY_APP: Application = {
    [AppKeys.Email]: IS_DEV ? "dev@thebhwgroup.com" : "",
    [AppKeys.FirstName]: IS_DEV ? "John" : "",
    [AppKeys.MiddleInitial]: IS_DEV ? "A" : "",
    [AppKeys.LastName]: IS_DEV ? "Smith" : "",
    [AppKeys.Address]: {
        [AddressKeys.AddressLine1]: IS_DEV ? "123 Some St" : "",
        [AddressKeys.AddressLine2]: "",
        [AddressKeys.City]: IS_DEV ? "Austin" : "",
        [AddressKeys.State]: IS_DEV ? "TX" : "",
        [AddressKeys.Zipcode]: IS_DEV ? "78757" : "",
    },
    [AppKeys.StudentStatus]: IS_DEV ? models.StudentStatus.HSEntering : "",
    [AppKeys.PrimaryPhone]: IS_DEV ? "(123) 123-1234" : "",
    [AppKeys.DateOfBirth]: IS_DEV ? "01/01/2000" : "",
    [AppKeys.SocialSecurity]: IS_DEV ? "123-12-1234" : "",
    [AppKeys.College]: IS_DEV ? "Texas A&M University" : "",
    [AppKeys.SavingsAccount]: IS_DEV ? true : null,
    [AppKeys.CheckingAccount]: IS_DEV ? true : null,
    [AppKeys.PaymentAccount]: IS_DEV ? "checking" : "",
    [AppKeys.CardSelection]: "",
    [AppKeys.AccountTotal]: IS_DEV ? "$1,000" : "",
    [AppKeys.AnnualGrossIncome]: IS_DEV ? "$1,000" : "",
    [AppKeys.Residency]: IS_DEV ? true : null,
    [AppKeys.AnnualObligation]: "",
    [AppKeys.UrlCode]: "",
    [AppKeys.InviteCode]: null,
    [AppKeys.ReferralCode]: null,
};

type RouterState = {
    email?: string;
    id?: string;
};

export function checkForDefunctCode(offerCode: string | undefined): string | null {
    if (offerCode) {
        return DEFUNCT_OFFER_CODES.includes(offerCode) ? null : offerCode;
    }
    return null;
}

const getDefaultCardSelection = (availableCardTypes: string[] | null, defaultCardType?: string) => {
    if (availableCardTypes) {
        const {cards} = getCards(availableCardTypes, defaultCardType);
        if (cards.length > 0) {
            return cards[0].value;
        }
    }
    return "";
};

export default function OrganicApplicationPage(
    props: RouteComponentProps<undefined, StaticContext, RouterState>,
) {
    const isMountedRef = useRef<boolean>(false);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const queryParams = queryStringHelpers.objectify(window.location.search);
    const windowUrl = window.location.href;

    const [application, setApplication] = useState({
        ...EMPTY_APP,
        [AppKeys.Email]: props.location.state?.email
            ? props.location.state.email
            : EMPTY_APP[AppKeys.Email],
        [AppKeys.UrlCode]: checkForDefunctCode(queryParams.cid),
        [AppKeys.ReferralCode]: queryParams.rid ? queryParams.rid : null,
    });

    const [validation, setValidation] = useState<validationLib.Validation>({});

    const [loading, setLoading] = useState(false);

    const [step, setStep] = useState(0);
    const [validationCount, setValidationCount] = useState(3);

    useEffect(() => {
        document.title = strings.title;
    }, []);

    usePageView(strings.title);

    useEffect(() => {
        const qs = queryStringHelpers.objectify(window.location.search);
        if (qs.m !== "300") {
            endpoints
                .visit(
                    getUserIds().visitID,
                    document.documentURI,
                    navigator.userAgent,
                    document.referrer,
                )
                .catch((err) => error.reportAxios(err, COMPONENT_NAME));
            callApplicationLead(leadEvents.FORM_VIEW, null, window.location.href);
        }
    }, []);

    const debouncedAppLead = useRef(createDebouncedAppLead(EMPTY_APP, null, window.location.href));
    useEffect(() => {
        if (!isMountedRef.current) {
            return;
        }
        Promise.resolve()
            .then(() => debouncedAppLead.current(application))
            .catch((err) => error.reportAxios(err, COMPONENT_NAME));
    }, [application]);

    const [supportedCardTypes, setSupportedCardTypes] = useState<string[]>([]);
    const [loadingCardTypes, setLoadingCardTypes] = useState(true);
    const [defaultCardBrand, setDefaultCardBrand] = useState<"VISA" | "MC">("VISA");
    const [showGalleryView, setShowGalleryView] = useState(false);
    useEffect(() => {
        const qs = queryStringHelpers.objectify(window.location.search);
        const urlCode = qs.urlCode;
        const cid = qs.cid;
        const code = checkForDefunctCode(cid ?? urlCode) ?? DEFAULT_URL_CODE;
        endpoints
            .getCardTypes(code)
            .then(({cardTypes, attributes}) => {
                setSupportedCardTypes(cardTypes);
                setApplication((prev) => ({
                    ...prev,
                    [AppKeys.CardSelection]: getDefaultCardSelection(
                        cardTypes,
                        attributes.defaultCardType,
                    ),
                }));
                if (cardTypes.find((t) => t.endsWith("_MC"))) {
                    setDefaultCardBrand("MC");
                }
                setShowGalleryView(!!attributes.multiImageSlider);
            })
            .catch((err) => error.reportAxios(err, COMPONENT_NAME))
            .finally(() => setLoadingCardTypes(false));
    }, []);

    function onContinue() {
        if (!hasFailingValidation(validation)) {
            const parsedApplication = applicationToApplicationValidate(
                application,
                false,
                defaultCardBrand,
            );
            setLoading(true);
            callApplicationLead(leadEvents.FORM_VALIDATE, null, windowUrl);
            endpoints
                .applicationValidate(parsedApplication)
                .then(() => {
                    callApplicationLead(leadEvents.FORM_DISCLOSURE, null, windowUrl);
                    setStep(1);
                    window.scrollTo(0, 0);
                })
                .catch((err) => setValidation(validationLib.getValidationObj(err)))
                .finally(() => {
                    setLoading(false);
                    setValidationCount((prev) => prev + 1);
                });
        }
    }

    function clearValidation(validationKey: AppKeys | AddressKeys | AddressValidationKeys) {
        setValidation((prev) => {
            if (prev.generic || (prev.errors && prev.errors[validationKey])) {
                const nextValidation = {
                    ...prev,
                };
                delete nextValidation.generic;
                if (prev && prev.errors) {
                    delete prev.errors[validationKey];
                }
                return nextValidation;
            }
            return prev;
        });
    }

    function onChangeApplicationField(name: AppKeys | AddressKeys, value: string | boolean) {
        if (isAddressKey(name)) {
            setApplication((prevApp) => ({
                ...prevApp,
                [AppKeys.Address]: {
                    ...prevApp[AppKeys.Address],
                    [name]: value,
                },
            }));
            const validationKey = getValidationKey(name as AddressKeys);
            clearValidation(validationKey);
        } else {
            setApplication((prevApp) => ({
                ...prevApp,
                [name]: value,
            }));
            clearValidation(name);
        }
    }

    function onBlurInput(e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;
        if (isApplicationKey(name) || isAddressKey(name)) {
            const {isValid, messages, validationKey} = validateApplicationField(name, value);
            if (isValid) {
                return;
            }

            if (validationKey !== null) {
                setValidation((prev) => ({
                    ...prev,
                    errors: {
                        ...prev?.errors,
                        [validationKey]: messages,
                    },
                }));
            }
        }
    }

    function onSubmit(
        agreeToElectronicCommunications: boolean,
        EBSVConsent: boolean,
        nscConsent: boolean,
    ) {
        const parsed = applicationToApplicationValidate(application, false, defaultCardBrand);
        setLoading(true);
        callApplicationLead(leadEvents.FORM_SUBMIT, null, windowUrl);

        endpoints
            .applicationSubmit(parsed, agreeToElectronicCommunications, EBSVConsent, nscConsent)
            .then((data) => {
                const {applicationId, status, instantCard} = data;

                if (status === models.ApplicationStatus.Approved && applicationId) {
                    let queryString = queryStringHelpers.addReferrerGCLID(
                        window.location.search,
                        document.referrer,
                    );

                    // If instantCard is true, set in query string params
                    // Even though this is not secure, changing the query string param
                    // Only allows the user to make a request for the instant card info
                    // Any invalid requests would return no values
                    if (instantCard) {
                        const qs = queryStringHelpers.objectify(queryString);
                        qs[queryStringHelpers.Keys.InstantCard] = "true";
                        queryString = queryStringHelpers.stringify(qs);
                    }

                    props.history.replace(`${routes.CONGRATULATIONS}${queryString}`, {
                        id: applicationId,
                    });
                    return;
                }

                let destination = routes.PROCESSING;
                switch (status) {
                    case models.ApplicationStatus.Duplicate:
                        destination = routes.DUPLICATE;
                        break;
                    case models.ApplicationStatus.DuplicateDsn:
                        destination = routes.DUPLICATE_DSN;
                        break;
                    case models.ApplicationStatus.ThankYou:
                        destination = routes.THANK_YOU;
                        break;
                    case models.ApplicationStatus.Secured:
                        destination = routes.SECURED_CARD;
                }

                let queryString = window.location.search;
                if (applicationId) {
                    queryString = queryStringHelpers.addDSN(queryString, applicationId);
                }
                if (destination === routes.PROCESSING) {
                    const qs = queryStringHelpers.objectify(queryString);
                    qs[queryStringHelpers.Keys.CardBrand] = defaultCardBrand;
                    queryString = queryStringHelpers.stringify(qs);
                }
                queryString = queryStringHelpers.addReferrerGCLID(queryString, document.referrer);
                const fullDestination = `${process.env.REACT_APP_BASE_PATH}${destination}${queryString}`;
                // NOTE: Using full round-trip page transition here for security reasons
                window.location.replace(fullDestination);
            })
            .catch((err) => {
                setValidation(validationLib.getValidationObj(err));
                callApplicationLead(leadEvents.FORM_VIEW, null, windowUrl);
                setStep(0);
            })
            .finally(() => {
                setLoading(false);
                setValidationCount((prev) => prev + 1);
            });
    }
    const firstInvalidKey = getFirstInvalidKey(validation, ValidationType.Application);

    function renderCardPicker() {
        if (loadingCardTypes) {
            return (
                <div className={styles.spinnerContainer}>
                    <Spinner isVisible={true} />
                </div>
            );
        }

        let validationMessage: string[] | undefined;
        if (validation.errors && validation.errors[AppKeys.CardSelection]) {
            validationMessage = validation.errors[AppKeys.CardSelection];
        }
        return (
            <div className={styles.cardPicker}>
                <CardPicker
                    cardId={application[AppKeys.CardSelection]}
                    onChange={(cardId: string) =>
                        onChangeApplicationField(AppKeys.CardSelection, cardId)
                    }
                    name={getFullName(
                        application[AppKeys.FirstName],
                        "",
                        application[AppKeys.LastName],
                    )}
                    validationMessage={validationMessage}
                    cardTypes={supportedCardTypes}
                />
            </div>
        );
    }

    const validCards = getCards(supportedCardTypes);
    let showCardPicker = loadingCardTypes || validCards.cardCategories.length > 1;
    if (validCards.cardCategories.length === 1) {
        showCardPicker =
            validCards.cardCategories[0].cardType === models.CardSelectionOptions.PhotocardVisa ||
            validCards.cardCategories[0].cardType === models.CardSelectionOptions.PhotocardMC;
    }

    return (
        <ErrorBoundary>
            <IdleTimeoutWrapper focusAfterContinue={null}>
                <Header />
                <main className={cn(styles.container, step !== 0 ? styles.hidden : undefined)}>
                    <div className={styles.innerContainer}>
                        <ApplicationForm
                            application={application}
                            continueLabel={strings.continue}
                            loading={loading}
                            onBlurInput={onBlurInput}
                            onChangeApplication={onChangeApplicationField}
                            firstInvalidKey={firstInvalidKey}
                            onContinue={onContinue}
                            validation={validation}
                            validationCount={validationCount}
                            renderCardPicker={showCardPicker ? renderCardPicker : undefined}
                            singleCardType={showCardPicker ? undefined : supportedCardTypes[0]}
                            continueDisabled={loadingCardTypes}
                            defaultCardBrand={defaultCardBrand}
                        />
                        {showGalleryView ? <GalleryView cardTypes={supportedCardTypes} /> : null}
                    </div>
                    <LegalInfo renderInterestRates={false} />
                </main>

                <div className={cn(styles.container, step !== 1 ? styles.hidden : undefined)}>
                    <DisclosureInfo
                        goBack={() => {
                            callApplicationLead(leadEvents.FORM_BACK, null, windowUrl);
                            setStep(0);
                        }}
                        onSubmit={onSubmit}
                        loading={loading}
                        applicationAddressState={application.address.state}
                    />
                </div>

                <Footer />
            </IdleTimeoutWrapper>
        </ErrorBoundary>
    );
}
